import React, { useCallback } from "react";
import { FlatList, Pressable, StyleSheet, Text, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type RootStackParamList from "../navigation/RootStackParamList";
import useHeaderTopPadding from "../components/AppHeader/useHeaderTopPadding";
import useInbox from "../hooks/useInbox";
import { auth } from "../firebase";
import { markInboxRead } from "../services/notificationInbox";
import type { InboxItem } from "../utils/inbox";

// In-app notification inbox screen (#133). Lists the user's notifications,
// marks one read on tap, and deep-links to the related conversation list.
export default function InboxScreen() {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const headerTopPadding = useHeaderTopPadding();
  const { items } = useInbox();

  const handlePress = useCallback(
    async (item: InboxItem) => {
      const uid = auth.currentUser?.uid;
      if (uid && !item.read) {
        try {
          await markInboxRead(uid, item.id);
        } catch (err) {
          console.error("Failed to mark inbox item read:", err);
        }
      }
      if (item.roomId) {
        navigation.navigate("MeetingsTabs", { screen: "MyMeetings" });
      }
    },
    [navigation],
  );

  return (
    <View style={[styles.screen, { paddingTop: headerTopPadding }]}>
      <Text style={styles.heading}>Notifications</Text>
      <FlatList
        data={items}
        keyExtractor={(item) => item.id}
        ListEmptyComponent={
          <Text style={styles.empty}>You&apos;re all caught up.</Text>
        }
        renderItem={({ item }) => (
          <Pressable
            style={[styles.row, !item.read && styles.rowUnread]}
            onPress={() => handlePress(item)}
          >
            <Text style={styles.title}>{item.title}</Text>
            <Text style={styles.body}>{item.body}</Text>
          </Pressable>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: "#F6F7F9", paddingHorizontal: 16 },
  heading: { fontSize: 22, fontWeight: "700", marginVertical: 16 },
  empty: { textAlign: "center", color: "#6B7280", marginTop: 48 },
  row: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 10,
  },
  rowUnread: { borderLeftWidth: 4, borderLeftColor: "#2563EB" },
  title: { fontSize: 16, fontWeight: "600", marginBottom: 4 },
  body: { fontSize: 14, color: "#374151" },
});
